import React, {useEffect, useMemo, useRef} from 'react';
import {Modal, View, Text, Platform, AccessibilityInfo} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import NetInfo from '@react-native-community/netinfo';
import {RootState} from '../../../../shared/store';
import {
  hideErrorModal,
  setIsNetworkError,
  showErrorModal,
  confirmOvertimeYes,
  confirmOvertimeNo,
} from '../../../../shared/store/errorSlice';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {globalErrorModalStyles} from '../../../../shared/components/feedback/GlobalErrorModal/GlobalErrorModal.styles';
import {
  navigate,
  navigationRef,
} from '../../../../shared/navigation/navigationRef';
import {intentionalNavFlag} from '@shared/navigation/intentionalNavFlag';
import {networkErrorManager} from '@features/auth/api/axiosInstance';
import Button from '../../../../shared/components/ui/Button/Button';
import WarningIcon from '../../../../shared/assets/icons/WarningIcon';
import {setActiveTab, triggerHomeRefresh} from '@shared/store/menuSlice';
import {setSelectedRepairId} from '@shared/store/repairSlice';
import {menuTabs} from '@features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';

import {useTranslation} from 'react-i18next';
import NoInternet from '@shared/assets/icons/NoInternet';
import TryAgain from '@shared/assets/icons/TryAgain';
import {hideLoader, showLoader} from '@shared/store/loaderSlice';
import {requestAuthenticate} from '@features/repairDetails/RepairDetailsScreenController';
import {getConfig} from '@shared/config/AppConfig';
import {syncPendingActions} from '@features/offline/SyncEngine';
import {buttonVariants} from '@shared/constants/global';
import {MODAL_SUPPORTED_ORIENTATIONS} from '@features/repairDetails/hooks/useSubmittingLoader';
import {errorHandler} from '@shared/constants/errorHandlers';

// Stable IDs for aria-labelledby / aria-describedby wiring on web.
const MODAL_TITLE_ID = 'global-error-modal-title';
const MODAL_SUBTITLE_ID = 'global-error-modal-subtitle';

/**
 * Moves screen-reader focus to a DOM element (web) or RN node (native).
 * findNodeHandle is lazy-required so it is never evaluated on the web bundle.
 */
function moveFocusToElement(ref: React.MutableRefObject<any>): void {
  if (!ref.current) {return;}

  if (Platform.OS === 'web') {
    // On web, ref.current is a real DOM element.
    const el = ref.current as HTMLElement;
    if (typeof el.focus === 'function') {
      // tabIndex="-1" makes the element programmatically focusable without
      // inserting it into the natural Tab order. Static text nodes should not
      // be Tab stops; screen readers read them via aria-labelledby / the dialog
      // role without requiring a tab stop.
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '-1');
      }
      el.focus({preventScroll: true});
    }
  } else {
    // Lazy require keeps findNodeHandle out of the web bundle evaluation path.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const {findNodeHandle} = require('react-native') as {
      findNodeHandle: (ref: any) => number | null;
    };
    const node = findNodeHandle(ref.current);
    if (node != null) {
      AccessibilityInfo.setAccessibilityFocus(node);
    }
  }
}

/**
 * Installs a keydown listener that traps Tab / Shift+Tab focus inside
 * `containerEl` while the modal is open.  Returns a cleanup function.
 */
function trapFocus(containerEl: HTMLElement): () => void {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {return;}

    const focusable = Array.from(
      containerEl.querySelectorAll<HTMLElement>(focusableSelectors),
    ).filter(el => !el.closest('[aria-hidden="true"]'));

    if (focusable.length === 0) {return;}

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}

const GlobalErrorModal = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const theme = useScreenTheme('globalErrorModal');
  const styles = useMemo(() => globalErrorModalStyles(theme), [theme]);

  // Ref for the modal title — primary focus target when modal opens.
  const modalTitleRef = useRef<any>(null);
  // Ref for the subtitle — fallback focus target when there is no title.
  const modalSubTitleRef = useRef<any>(null);
  // Ref for the dialog container — used to install the focus trap on web.
  const modalContainerRef = useRef<any>(null);

  const {
    isVisible,
    title,
    subTitle,
    message,
    ctas,
    screen,
    noTranslation,
    isNetworkError,
    icon,
    underlineMessage,
  } = useSelector((state: RootState) => state.error);

  const {selectedTechnician, createdUserId} = useSelector(
    (state: RootState) => state.technician,
  );

  useEffect(() => {
    if (isVisible && Platform.OS !== 'android') {
      dispatch(hideLoader());
    }
  }, [isVisible]);

  // Track the element that had focus before the modal opened so we can
  // restore it when the modal closes (web only).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Move accessibility focus into the modal whenever it becomes visible so
  // screen readers announce the dialog immediately without the user needing
  // to navigate to it first.
  useEffect(() => {
    if (!isVisible) {return;}

    if (Platform.OS === 'web') {
      // Save the currently-focused element so focus can be restored on close.
      previouslyFocusedRef.current =
        (typeof document !== 'undefined' ? document.activeElement as HTMLElement : null);
    }

    // 300 ms delay lets the fade-in animation finish before shifting focus,
    // preventing a race condition on iOS/Android.
    const timer = setTimeout(() => {
      if (Platform.OS === 'web') {
        // On web: prefer to focus the dialog container itself first so the
        // screen reader announces it as a dialog, then let natural Tab order
        // move the user to the title / buttons.
        if (modalContainerRef.current) {
          const el = modalContainerRef.current as HTMLElement;
          if (!el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
          }
          el.focus({preventScroll: true});
        } else {
          // Fallback: focus title or subtitle text.
          const targetRef =
            title && title !== '' ? modalTitleRef : modalSubTitleRef;
          moveFocusToElement(targetRef);
        }
      } else {
        // Native: move VoiceOver / TalkBack focus to title or subtitle.
        const targetRef =
          title && title !== '' ? modalTitleRef : modalSubTitleRef;
        moveFocusToElement(targetRef);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isVisible, title]);

  // Install a focus trap on web so Tab / Shift+Tab cannot leave the dialog.
  // aria-modal="true" handles screen-reader background exclusion; we do NOT
  // use the inert attribute on body siblings because the RN Web modal portal
  // is appended asynchronously and the timing makes it impossible to reliably
  // identify the portal root — any mistake marks the modal itself inert and
  // freezes the entire application.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {return;}

    let trapCleanup: (() => void) | undefined;

    // Use requestAnimationFrame to ensure the modal portal is fully mounted
    // in the DOM before we query focusable elements inside it.
    const rafId = requestAnimationFrame(() => {
      if (modalContainerRef.current) {
        trapCleanup = trapFocus(modalContainerRef.current as HTMLElement);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (typeof trapCleanup === 'function') {trapCleanup();}
      // Restore focus to the element that was focused before the modal opened.
      if (previouslyFocusedRef.current &&
          typeof previouslyFocusedRef.current.focus === 'function') {
        previouslyFocusedRef.current.focus({preventScroll: true} as any);
        previouslyFocusedRef.current = null;
      }
    };
  }, [isVisible]);

  const handleAction = async (cta: {
    label: string;
    type: string;
    primary?: boolean;
    payload?: Record<string, any>;
  }) => {
    const {type} = cta;
    switch (type) {
      case 'DISMISS':
        networkErrorManager.rejectAllRequests({
          message: 'Request dismissed by user.',
        });
        dispatch(hideErrorModal());
        if (subTitle?.includes(errorHandler?.notInActivity)) {
          setTimeout(() => {
            dispatch(setActiveTab(menuTabs?.home as string));
            dispatch(setSelectedRepairId(''));
            dispatch(triggerHomeRefresh());
          }, 350);
        }
        if (subTitle?.includes(errorHandler?.alreadyInShift)) {
          setTimeout(() => {
            dispatch(setActiveTab(menuTabs?.home as string));
            dispatch(setSelectedRepairId(''));
            dispatch(triggerHomeRefresh());
          }, 350);
        }
        break;

      case 'YES':
        dispatch(confirmOvertimeYes());
        dispatch(hideErrorModal());
        break;

      case 'NO':
        // Triggers the overtime-declined retry in the calling screen
        // (technicianDeclinedOvertime = true) but does NOT navigate away.
        dispatch(confirmOvertimeNo());
        dispatch(hideErrorModal());
        break;

      case 'NAVIGATE':
        dispatch(hideErrorModal());
        if (screen === 'repairDetails') {
          // FIX: Delay navigation dispatches until after the iOS Modal fade-out
          // animation (~300 ms) completes. Dispatching setActiveTab/setSelectedRepairId
          // synchronously while the modal is still animating out causes the
          // presenting UIViewController to unmount mid-animation, producing a
          // UIKit deadlock that freezes all touch input on iOS.
          setTimeout(() => {
            dispatch(setActiveTab(menuTabs?.repairs as string));
            dispatch(setSelectedRepairId(''));
          }, 350);
        }
        break;

      case 'REQUEST_AUTHORIZATION': {
        try {
          dispatch(showLoader());
          const applicationBaseUrl =
            (getConfig()?.API_BASE_URL ?? '').replace(/\/+$/, '');
          const result = await requestAuthenticate({
            documentId: cta.payload?.documentId ?? '',
            technicianId: selectedTechnician?.selectedTechnicianId ?? '',
            userId: createdUserId ?? '',
            navigateToHome: false,
            applicationBaseUrl,
            applicationVirtualPath: '',
          });
          dispatch(hideLoader());
          if (result.success) {
            dispatch(
              showErrorModal({
                title: t('createFlow.common.modalTitles.info'),
                subTitle: 'Authorization request sent successfully to',
                message: result.toEmail ?? '',
                noTranslation: true,
                underlineMessage: true,
                ctas: [{label: 'Ok', type: 'DISMISS'}],
                screen: screen ?? '',
              }),
            );
          } else {
            dispatch(
              showErrorModal({
                title: t('repairDetails.authFailedTitle'),
                subTitle: result.message ?? '',
                message: '',
                ctas: [
                  {
                    label: t('repairDetails.cancelAuthorizationCTA'),
                    type: 'DISMISS',
                    primary: false,
                  },
                  {
                    label: t('repairDetails.requestAuthorizationAgainCTA'),
                    type: 'REQUEST_AUTHORIZATION',
                    payload: cta.payload,
                  },
                ],
                screen: screen ?? '',
                noTranslation: !!result.message,
              }),
            );
          }
        } catch {
          dispatch(hideLoader());
        }
        break;
      }

      case 'RETRY':
        if (screen === 'backToAgencySelection') {
          dispatch(hideErrorModal());
          // Use reset() so the full navigation stack is replaced with just
          // AgencySelectionScreen — a simple navigate() only pushes/goes-to
          // the screen without removing MainLayoutScreen from the stack, and
          // on iOS the MainLayoutScreen beforeRemove guard intercepts it and
          // shows the logout modal instead of allowing navigation.
          // Delay 350 ms so the Modal fade-out animation completes before the
          // stack reset fires — matches the NAVIGATE case pattern above.
          intentionalNavFlag.current = true;
          setTimeout(() => {
            navigationRef.current?.reset({
              index: 0,
              routes: [{name: 'AgencySelectionScreen'}],
            });
          }, 350);
          break;
        }
        const state = await NetInfo.fetch();
        if (state.isConnected) {
          try {
            dispatch(hideErrorModal());
            dispatch(showLoader());
            await networkErrorManager.retryAllRequests();
            dispatch(setIsNetworkError({isNetworkError: false}));
            dispatch(hideLoader());
            //await syncPendingActions();
          } catch (error) {
            console.error('Retry failed:', error);
          }
        } else {
          // Re-show the modal if still offline
          dispatch(setIsNetworkError({isNetworkError: true}));
          dispatch(
            showErrorModal({
              title: t('createFlow.common.modalTitles.error'),
              subTitle: t('common.networkError.subTitle'),
              message: t('common.networkError.desc'),
              ctas: [{label: t('common.networkError.tryAgaincta'), type: 'RETRY'}],
              screen: '',
              noTranslation: false,
            }),
          );
        }
        break;

      // case 'NAVIGATE': {
      //   const {payload} = cta as {payload?: {screen?: string}};
      //   if (payload?.screen) {
      //     navigate('LoginScreen');
      //   }
      //   dispatch(hideErrorModal());
      //   break;
      // }

      default:
        dispatch(hideErrorModal());
    }
  };

  const renderModalHeader = () =>
    title && title !== '' ? (
      <View style={styles.headerTitleContainer}>
        {icon && (
          <View
            style={styles.iconContainer}
            accessible={false}
            importantForAccessibility="no"
            {...(Platform.OS === 'web' ? ({'aria-hidden': 'true'} as any) : {})}>
            {icon}
          </View>
        )}
        <Text
          ref={modalTitleRef}
          style={styles.headerTitle}
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel={noTranslation ? title : t(title)}
          // The stable id is referenced by aria-labelledby on the dialog.
          // tabIndex is intentionally omitted — static text should not be
          // in the tab order; aria-labelledby wires the title to the dialog
          // container so screen readers announce it without a tab stop.
          {...(Platform.OS === 'web'
            ? {id: MODAL_TITLE_ID}
            : {})}
          testID="modalHeader">
          {noTranslation ? title : t(title)}
        </Text>
      </View>
    ) : null;

  const renderSubTitleMessage = () => (
    <>
      {underlineMessage && subTitle && message ? (
        // Single-line: static text + underlined email in one <Text> block.
        <Text
          ref={modalSubTitleRef}
          testID="global-error-modal-subTitle"
          style={styles.title}
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={`${noTranslation ? subTitle : t(subTitle)} ${noTranslation ? message : t(message)}`}
          {...(Platform.OS === 'web'
            ? {id: MODAL_SUBTITLE_ID}
            : {})}>
          {noTranslation ? subTitle : t(subTitle)}{' '}
          <Text style={{textDecorationLine: 'underline'}}>
            {noTranslation ? message : t(message)}
          </Text>
        </Text>
      ) : (
        <>
          {subTitle && subTitle !== '' && (
            <Text
              ref={modalSubTitleRef}
              testID="global-error-modal-subTitle"
              style={styles.title}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={noTranslation ? subTitle : t(subTitle)}
              {...(Platform.OS === 'web'
                ? {id: MODAL_SUBTITLE_ID}
                : {})}>
              {noTranslation ? subTitle : t(subTitle)}
            </Text>
          )}
          {message && message !== '' && (
            <Text
              testID="global-error-modal-message"
              style={styles.message}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={noTranslation ? message : t(message)}>
              {noTranslation ? message : t(message)}
            </Text>
          )}
        </>
      )}
    </>
  );

  const renderButtons = () => (
    <View style={styles.buttonContainer}>
      {ctas?.map((cta, index: React.Key | null | undefined) => (
        <Button
          key={index}
          testID={`global-error-modal-cta-${cta.type}`}
          title={noTranslation ? cta.label : t(cta.label)}
          onPress={() => handleAction(cta)}
          fullWidth={false}
          variant={
            cta.primary === false
              ? buttonVariants.secondary
              : buttonVariants.primary
          }
          showLeftIcon
          customLeftIcon={isNetworkError && <TryAgain />}
          customIconStyles={styles.buttonContainerTextStyles}
          containerStyle={cta.primary === false && styles.cancelContainerStyle}
          customTextStyle={
            cta.primary === false && styles.cancelCustomTextStyle
          }
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={
            noTranslation ? cta.label : t((cta?.label ?? '') as string)
          }
          accessibilityHint={
            cta.type === 'RETRY'
              ? t('common.accessibility.globalErrorModal.retryHint')
              : cta.type === 'DISMISS'
              ? t('common.accessibility.globalErrorModal.dismissHint')
              : cta.type === 'NAVIGATE'
              ? t('common.accessibility.globalErrorModal.navigateHint')
              : t('common.accessibility.globalErrorModal.performsActionHint')
          }
        />
      ))}
    </View>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return Platform?.OS === 'web' ? (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_SUPPORTED_ORIENTATIONS}
      accessibilityViewIsModal={true}>
      <View style={styles.container}>
        <View
          ref={modalContainerRef}
          style={styles.modal}
          accessible={true}
          importantForAccessibility="yes"
          {...({
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': title && title !== '' ? MODAL_TITLE_ID : undefined,
            'aria-describedby':
              subTitle && subTitle !== '' ? MODAL_SUBTITLE_ID : undefined,
            // tabIndex="-1" makes the container programmatically focusable
            // (so we can call .focus() on it to announce the dialog to screen
            // readers) without inserting it into the natural Tab order.
            tabIndex: -1,
            // Data attribute used by lockBackgroundInert as a fallback selector
            // to identify the modal portal when modalContainerRef is unavailable.
            'data-global-error-modal': 'true',
          } as any)}>
          <View style={styles.header}>{renderModalHeader()}</View>
          <View style={styles.titleContainer}>
            <View style={styles.titleSubContainer}>
              {renderSubTitleMessage()}
            </View>
          </View>
          {renderButtons()}
        </View>
      </View>
    </Modal>
  ) : (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_SUPPORTED_ORIENTATIONS}
      accessibilityViewIsModal={true}>
      <View style={styles.container}>
        <SafeAreaView style={styles.modal} accessible={false}>
          <View style={styles.header}>{renderModalHeader()}</View>
          <View style={styles.titleContainer}>
            <View style={styles.titleSubContainer} accessible={false}>
              {renderSubTitleMessage()}
            </View>
          </View>
          {renderButtons()}
        </SafeAreaView>
      </View>
    </Modal>
  );
};

export default GlobalErrorModal;
